import { useCMSReady } from "../../../../domain/CMS/useCMSReady"
import useIsMobile from "../../hooks/useIsMobile"
import Button from "../Button"
import Label from "../Label"
import RowContainer from "../Layout/RowContainer"
import Select from "../Select/Select"
import { Typography, typographyTypes } from "../Typography"
import { useMemo } from "react"
import PaginationSkeleton from "./PaginationSkeleton"
import { ROWS_PER_PAGE_OPTIONS } from "./useTablePagination"
import { cn } from "../../../../utils/classes"
import Icon from "../Icon"

interface TablePaginationProps {
  rowsPerPage: number
  currentPage: number
  numberOfPages: number
  showingRowsMessage: string
  handlePageChange: (page: number) => Promise<void>
  handleRowsPerPageChange: (rowsPerPage: number) => Promise<void>
  loading?: boolean
  controlledTableId?: string
}

export const TablePagination: React.FC<TablePaginationProps> = ({
  rowsPerPage,
  currentPage,
  numberOfPages,
  showingRowsMessage,
  handlePageChange,
  handleRowsPerPageChange,
  loading = false,
  controlledTableId,
}) => {
  const isMobile = useIsMobile()
  const {
    cms: { common: commonText },
  } = useCMSReady(["common"])

  const defaultOption = useMemo(() => {
    const defaultOption = ROWS_PER_PAGE_OPTIONS.find(
      (option) => option.value === rowsPerPage,
    )
    return defaultOption || ROWS_PER_PAGE_OPTIONS[0]
  }, [rowsPerPage])

  return loading ? (
    <PaginationSkeleton />
  ) : (
    <nav
      aria-label={commonText?.tablePaginationAriaLabel ?? "Table pagination"}
      aria-controls={controlledTableId}
    >
      <RowContainer styleOverrides="bg-white rounded-lg border-solid border-[1px] border-gray-200 flex flex-wrap items-center min-w-[320px] !box-border justify-between px-[16px] py-[18px]">
        <Typography
          text={showingRowsMessage}
          variant={typographyTypes.HELPER}
        />
        <div
          className={cn("flex items-center space-x-1", { "!mx-0": isMobile })}
        >
          <Select
            ariaLabel={commonText?.rowsPerPageSelectLabel || ""}
            inputId="rowsPerPageSelect"
            label={
              <Label htmlFor="rowsPerPageSelect" styleOverrides="mr-[-20px]">
                <Typography
                  text={commonText?.rowsPerPageSelectLabel}
                  variant={typographyTypes.HELPER}
                />
              </Label>
            }
            onChange={(option) => handleRowsPerPageChange(option!.value)}
            labelPosition="left"
            borderless
            isSearchable={false}
            isClearable={false}
            placeholder=""
            size="sm"
            styleOverrides="text-sm"
            options={ROWS_PER_PAGE_OPTIONS}
            defaultValue={defaultOption}
            useDefaultBorderlessIndicator
            controlStyleOverrides="!m-0 !p-0 space-x-0 !min-h-[24px]"
            menuStyleOverrides="!z-20"
            valueContainerStyleOverrides="!pr-0 !pl-2"
            indicatorsContainerStyleOverrides="!px-0"
            containerStyleOverrides="h-4"
            menuPlacement="top"
          />
          {numberOfPages > 1 && (
            <div className="flex space-x-1 items-center ml-4">
              {currentPage > 1 && (
                <Button
                  styleOverrides="p-0 mr-2"
                  onClick={async () => await handlePageChange(currentPage - 1)}
                  type="transparent"
                  disabled={currentPage === 1}
                  roundedFull={false}
                  buttonLabel={
                    <Icon
                      iconType="chevronLeft"
                      size="md"
                      styleOverrides="mb-[-5px]"
                    />
                  }
                />
              )}
              <div className="flex">
                <Typography
                  text={currentPage}
                  variant={typographyTypes.NORMAL}
                  styleOverrides="text-black"
                />
                <Typography
                  text={`/${numberOfPages}`}
                  variant={typographyTypes.HELPER}
                />
              </div>
              {currentPage < numberOfPages && (
                <Button
                  styleOverrides="p-0 mr-2"
                  onClick={() => handlePageChange(currentPage + 1)}
                  type="transparent"
                  roundedFull={false}
                  buttonLabel={
                    <Icon
                      iconType="chevronRight"
                      size="md"
                      styleOverrides="mb-[-5px]"
                    />
                  }
                />
              )}
            </div>
          )}
        </div>
      </RowContainer>
    </nav>
  )
}
